import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { buildInbox, partitionAcknowledged } from "./eventQueueInbox";
import {
  eventQueueAcknowledgementsQueryOptions,
  eventQueueBackgroundJobsQueryOptions,
  eventQueueFailuresQueryOptions,
  eventQueueOverviewQueryOptions,
  eventQueueWorkersQueryOptions,
} from "./eventQueueReactQuery";

/**
 * Everything the Inbox is built from. The rail, the nav and the Inbox page all
 * read it, and React Query shares each request between them. `items` is what
 * still needs a person; `acknowledged` is what someone is already on.
 * Away from Queues the rail passes a slower `refetchInterval`.
 */
export function useEventQueueInbox(options: { enabled?: boolean; refetchInterval?: number } = {}) {
  const shared = {
    enabled: options.enabled ?? true,
    ...(options.refetchInterval ? { refetchInterval: options.refetchInterval } : {}),
  };
  const overview = useQuery({ ...eventQueueOverviewQueryOptions(), ...shared });
  const failures = useQuery({ ...eventQueueFailuresQueryOptions(null, "all"), ...shared });
  const workers = useQuery({ ...eventQueueWorkersQueryOptions(), ...shared });
  const schedules = useQuery({ ...eventQueueBackgroundJobsQueryOptions(), ...shared });
  const acknowledgements = useQuery({ ...eventQueueAcknowledgementsQueryOptions(), ...shared });
  const all = useMemo(
    () =>
      buildInbox({
        failures: failures.data,
        overview: overview.data,
        schedules: schedules.data,
        workers: workers.data,
      }),
    [failures.data, overview.data, schedules.data, workers.data],
  );
  // Judged at each refresh's time, so an expired acknowledgement brings its item back.
  const now = overview.dataUpdatedAt || Date.now();
  const { acknowledged, open } = useMemo(
    () => partitionAcknowledged(all, acknowledgements.data?.acknowledgements ?? [], now),
    [all, acknowledgements.data, now],
  );
  return { acknowledged, acknowledgements, failures, items: open, overview, schedules, workers };
}

/** Queue display names by engineering key, so no page has to show `github_pr_reconcile`. */
export function useQueueLabels(): Record<string, string> {
  const overview = useQuery(eventQueueOverviewQueryOptions());
  return useMemo(
    () =>
      Object.fromEntries(
        (overview.data?.queues ?? []).map((queue) => [
          queue.queueName,
          queue.displayName || queue.queueName,
        ]),
      ),
    [overview.data],
  );
}

export function queueLabel(labels: Record<string, string>, queueName: string | null): string {
  if (!queueName) return "No queue";
  return labels[queueName] ?? queueName;
}
